import { useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'

import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { PasswordRevealButton } from '../components/ui/PasswordRevealButton'
import { useAuth } from '../context/AuthContext'
import { APP_DISPLAY_NAME } from '@/lib/appDisplayName'
import { AuthLayout } from '../layouts/AuthLayout'

export default function Login() {
  const { login, isAuthenticated, loading, error, clearError } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = location.state?.from?.pathname || '/'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    clearError()
    try {
      await login({ email, password })
      navigate(from, { replace: true })
    } catch {
      /* error surfaced via context */
    }
  }

  const brandInitial = APP_DISPLAY_NAME.charAt(0).toUpperCase() || '—'

  return (
    <AuthLayout>
      <Card accent className="w-full max-w-md shadow-[var(--shadow-modal)]">
        <div className="mb-7 text-center">
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-lg font-bold text-primary-foreground shadow-sm">
            {brandInitial}
          </div>
          <p className="mb-2 text-[0.6875rem] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Administrator
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Welcome to {APP_DISPLAY_NAME}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">Secure administrator sign-in</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            id="email"
            type="email"
            label="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={1}
            endAdornment={
              <PasswordRevealButton
                visible={showPassword}
                onToggle={() => setShowPassword((v) => !v)}
                labelShow="Show password"
                labelHide="Hide password"
              />
            }
          />

          <p className="text-right text-sm">
            <Link to="/forgot-password" className="font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
              Forgot password?
            </Link>
          </p>

          {error ? (
            <p className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </Card>
    </AuthLayout>
  )
}
